import { graphql, Link } from 'gatsby';
import React, {
    ComponentProps,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Maybe, NewShopPageQuery, ProductColors, SanityBlock } from '../../../graphql-types';
import { FakeProductCard, ProductCard } from '../../components/ProductCard';
import SEO from '../../components/seo';
import Layout from '../../components/shared/layout';
import { buttonReset, linkReset, secondaryButton, space, weight } from '../../components/styles';
import { I18n, I18nStr, I18nText, I18nTextBr, useI18n, useLanguage } from '../../i18n';
import { ILayoutData, Language, TPageTable } from '../../interfaces';
import { useQueryParam } from '../../lib/hooks';
import { createGroupMap } from '../../lib/utils';
import { desktopMedia, padding } from './constants';
import constants from '../../constants';
import styled from 'styled-components';
import { GenderId, translateGender } from '../../gender';
import { NewNavigationBar } from '../../components/NewNavigationBar';
import { getMetadataFromPageTable } from '../../helpers/get-metadata';
import translate from '../../translations';
import { Breadcrumbs } from '../../components/tailwind/molecules/breadcrumbs';
import { Container } from '../../components/tailwind/atoms/container';
import { ContentBlocks } from '../../components/tailwind/content-blocks';
import { RichText } from '../../components/tailwind/molecules/rich-text';
import { getCategoryPath } from '../../helpers/get-path';

type Products = NewShopPageQuery['allProduct']['nodes'];
type Product = NewShopPageQuery['allProduct']['nodes'][0];
type FakeProducts = NewShopPageQuery['allSanityFakeProduct']['nodes'];
type FakeProduct = NewShopPageQuery['allSanityFakeProduct']['nodes'][0];

type Blocks = ComponentProps<typeof ContentBlocks>['blocks'];

const createProductBreadcrumbs = ({
    gender,
    categoryTitle,
    language,
    path,
}: {
    gender?: GenderId;
    categoryTitle?: string;
    language: Language;
    path: string;
}) => {
    const breadcrumbs = [
        {
            title: translate(language, 'products'),
            url: `/${language}/shop`,
        },
    ];

    if (gender) {
        breadcrumbs.push({
            title: translateGender(gender, language),
            url: `/${language}/shop/${gender}`,
        });
    }
    if (categoryTitle) {
        breadcrumbs.push({
            title: categoryTitle || '',
            url: path,
        });
    }

    return breadcrumbs;
};

export const ShopPageWrapper: FC<{
    pageContext: {
        pageTable: TPageTable;
        layoutData: ILayoutData;
        chosenCategory?: {
            id: string;
            title: string;
            metaTitle?: { nb?: string | null; en?: string | null } | null;
            metaDescription?: { nb?: string | null; en?: string | null } | null;
            description?: {
                nb?: SanityBlock | null;
                en?: SanityBlock | null;
            };
            blocks?: Blocks;
            bottomBlocks?: Blocks;
        };
        gender?: GenderId;
        noindex?: boolean;
    };
    data: NewShopPageQuery;
}> = ({ pageContext: { pageTable, layoutData, chosenCategory, gender, noindex }, data }) => {
    const { language } = layoutData;

    let chosenCategoryTitle = chosenCategory?.metaTitle?.[language] || chosenCategory?.title;

    const breadCrumbs = createProductBreadcrumbs({
        gender,
        categoryTitle: chosenCategoryTitle,
        language,
        path: pageTable[language],
    });

    let title =
        gender && chosenCategory
            ? `${chosenCategoryTitle} – ${translateGender(gender, language)}`
            : gender
            ? translateGender(gender, language)
            : chosenCategory
            ? chosenCategoryTitle
            : 'Products';

    const metadata = getMetadataFromPageTable({
        pageTable: pageTable,
        language: language,
        withDefaultTag: true,
    });

    return (
        <Layout layoutData={layoutData} pageTable={pageTable}>
            <SEO
                title={title}
                description={chosenCategory?.metaDescription?.[language]}
                lang={language}
                alternates={metadata?.alternates}
                canonicalURL={metadata?.canonicalUrl}
                noindex={noindex}
            />

            <Container>
                <Breadcrumbs className="my-4 md:my-6" language={language} links={breadCrumbs} />
            </Container>
            <ShopPage {...{ data }} chosenCategory={chosenCategory} gender={gender} />
        </Layout>
    );
};

const getUniqueColors = (products: NewShopPageQuery['allProduct']['nodes']) => {
    const hexSet: Set<ProductColors['hex']> = new Set();
    const uniqueColors: ProductColors[] = [];

    products.forEach(product => {
        product?.colors?.map(color => {
            if (color && !hexSet.has(color.hex)) {
                hexSet.add(color.hex);
                uniqueColors.push(color);
            }
        });
    });

    return uniqueColors;
};

const ShopPage: FC<{
    data: NewShopPageQuery;
    chosenCategory?: {
        id: string;
        title: string;
        metaTitle?: { nb?: string | null; en?: string | null } | null;
        metaDescription?: { nb?: string | null; en?: string | null } | null;
        description?: {
            nb?: SanityBlock | null;
            en?: SanityBlock | null;
        };
        description_men?: {
            nb?: SanityBlock | null;
            en?: SanityBlock | null;
        };
        blocks?: Blocks;
        bottomBlocks?: Blocks;
    };
    gender?: GenderId;
}> = ({ data, chosenCategory, gender }) => {
      const lang = useLanguage();
    const crowdfundingSlug = data.crowdfundingTexts?.slug?.[lang]?.current || '';
    const crowdsourcingSlug = data.crowdsourcingTexts?.slug?.[lang]?.current || '';
    const shopPageData = data.sanityShopPage;
    const [filters, setFilters] = useQueryParam('t');
    const [colors, setColors] = useQueryParam('color');

    const { blocks = [], bottomBlocks = [] } = chosenCategory || {};

    const { s } = useI18n();
    const clothingTypes = data.allSanityClothingType.nodes;

    const description =
        gender === 'men' && chosenCategory?.description_men
            ? chosenCategory.description_men[lang]
            : chosenCategory?.description?.[lang] ?? '';

    const uniqueColors = useMemo(() => {
        //calls the getUniqueColors function and caches the results
        return getUniqueColors(data.allProduct.nodes);
    }, [data.allProduct.nodes]);

    const matchesFilter = useCallback(
        (product: NewShopPageQuery['allProduct']['nodes'][0]) => {
            if (!filters) {
                return true;
            }

            if (filters.length === 0) {
                return true;
            }

            // Change this to .every to change filters to AND from OR
            // when it is .some the filters are OR
            return filters.some(filter => {
                if (product.collection?.slug?.[lang]?.current === filter) {
                    return true;
                }
                if (filter === crowdfundingSlug && product.productState === 'crowdFunding') {
                    return true;
                }
                if (filter === crowdsourcingSlug && product.productState === 'crowdSourcing') {
                    return true;
                }
                return false;
            });
        },
        [crowdfundingSlug, crowdsourcingSlug, filters, lang]
    );

    const matchesColor = useCallback(
        (product: NewShopPageQuery['allProduct']['nodes'][0]) => {
            if (!colors) {
                return true;
            }
            if (colors.length === 0) {
                return true;
            }
            return colors.some(color => {
                if (
                    product.colors?.some(
                        productColor => productColor?.hex === color || productColor?.title === color
                    )
                ) {
                    return true;
                }
                return false;
            });
        },
        [colors]
    );

    const matchesGender = useCallback(
        (product: NewShopPageQuery['allProduct']['nodes'][0]) => {
            if (!gender) {
                return true;
            }

            if (
                (product.clothingType?.gender === 'all' ||
                    product.clothingType?.gender === gender) &&
                (!product.sex ||
                    product.sex.length == 0 ||
                    product.sex.includes(gender) ||
                    // If there is any sex that is something else than man or woman (for instance not_applicable), we show the product to both men and women
                    product.sex.some(s => s !== 'man' && s !== 'women'))
            ) {
                return true;
            }
            return false;
        },
        [gender]
    );

    const matchesClothingType = useCallback(
        (product: NewShopPageQuery['allProduct']['nodes'][0]) => {
            if (product.clothingType?.id === chosenCategory?.id) {
                return true;
            }
            if (!chosenCategory) {
                return true;
            }
            return false;
        },
        [chosenCategory]
    );

    const navigationMatches = useMemo(
        () =>
            createGroupMap(
                data.allProduct.nodes
                    .filter(product => {
                        return matchesGender(product);
                    })
                    .map(product => [product.clothingType?.id || '', product])
            ),
        [data.allProduct.nodes, matchesGender]
    );

    const productByClothingType = useMemo(
        () =>
            createGroupMap(
                data.allProduct.nodes
                    .filter(product => {
                        return (
                            matchesFilter(product) &&
                            matchesColor(product) &&
                            matchesClothingType(product) &&
                            matchesGender(product)
                        );
                    })
                    .map(product => [product.clothingType?.id || '', product])
            ),
        [data.allProduct.nodes, matchesFilter, matchesColor, matchesGender, matchesClothingType]
    );

    const fakeProducts = useMemo(
        () =>
            createGroupMap<FakeProduct>(
                data.allSanityFakeProduct.nodes.map(product => [
                    product.clothingType?.id || '',
                    product,
                ])
            ),
        [data.allSanityFakeProduct.nodes]
    );

    const navigationItems = useMemo(
        () =>
            clothingTypes.map(type => {
                const g = type.gender === gender || type.gender === 'all';
                return {
                    title: s(type.title),
                    slug:
                        type?.slug?.[lang]?.current ||
                        s(type.title).split(' ').join('-').toLowerCase(),
                    disabled:
                        !g ||
                        !(
                            (navigationMatches[type.id] && navigationMatches[type.id].length > 0) ||
                            (fakeProducts[type.id] && fakeProducts[type.id].length > 0)
                        ),
                };
            }),
        [clothingTypes, gender, s, lang, navigationMatches, fakeProducts]
    );

    const TitleH1 = styled.h1`
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        font-size: clamp(100px, 15vw, 100px);
        line-height: clamp(80px, 15vw, 100px);
        letter-spacing: 1%;
        font-family: 'FK-Screamer', Impact, sans-serif;
        letter-spacing: 1%;
        text-transform: uppercase;
        align-self: center;
        text-align: center;
        padding: 0 5vw;
        margin-top: 0.5em;
    `;

    const Description = styled.div`
        font-size: 1.25rem;
        text-align: center;
        max-width: 800px;
        margin: 0 auto;
        margin-bottom: 1rem;
        margin-top: 0.5rem;
    `;

    const title = chosenCategory?.title || (gender && translateGender(gender, lang));

    return (
        <div
            css={`
                box-sizing: border-box;
                *,
                *:before,
                *:after {
                    box-sizing: inherit;
                }
                width: 100%;
                max-width: 1240px;

                margin: 0 auto;
                padding: 0 ${padding};

                @media (min-width: 1550px) {
                    max-width: 2100px;
                    width: 80%;
                }
            `}
        >
            {title && <TitleH1>{title}</TitleH1>}

            {description ? (
                <Description>
                    <RichText value={description} variant="body-sm" desktopVariant="body-md" />
                </Description>
            ) : null}

            {gender && <NewNavigationBar items={navigationItems} language={lang} gender={gender} />}
            {blocks ? <ContentBlocks blocks={blocks} /> : null}
            <Filters {...{ filters, setFilters, data }} />
            {/*<ColorFilters {...{ colors, setColors, uniqueColors }} />*/}
            {clothingTypes.map(type => {
                const correctGender = !gender || type.gender === gender || type.gender === 'all';
                const hasProducts = productByClothingType[type.id] || fakeProducts[type.id];
                return hasProducts && correctGender ? (
                    <Products
                        key={type.id}
                        gender={gender}
                        id={`type_${type.id}`}
                        products={productByClothingType[type.id]}
                        fakeProducts={fakeProducts[type.id]}
                    />
                ) : null;
            })}
            {bottomBlocks ? <ContentBlocks blocks={bottomBlocks} /> : null}
            {shopPageData && shopPageData.footerStatementHeader && (
                <div
                    css={`
                        text-align: center;
                        max-width: ${space(103)};
                        margin: 0 auto ${space(22)};
                        p {
                            margin: 0;
                        }

                        h2 {
                            font-size: 63px;
                            line-height: 1.1;
                            margin-bottom: ${space(3)};

                            @media (max-width: 800px) {
                                font-size: 48px;
                            }
                        }

                        a {
                            ${linkReset};
                            display: block;
                            ${secondaryButton};
                            margin-bottom: ${space(3)};
                            ${weight.medium};
                            border: 2px solid #f2f2f2;
                            line-height: 48px;
                            padding: 0 ${space(3)};
                        }
                    `}
                >
                    <h2>
                        <I18nTextBr>{shopPageData.footerStatementHeader}</I18nTextBr>
                    </h2>
                    <div
                        css={`
                            margin-bottom: ${space(3)};
                        `}
                    >
                        <I18nText>{shopPageData.footerStatementContent}</I18nText>
                    </div>
                    <div
                        css={`
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        `}
                    >
                        {(shopPageData.footerStatementLinks || []).map(item => (
                            <Link key={item?._key} to={getLink(item, lang)}>
                                <I18nStr>{item?.title}</I18nStr>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

type MenuItem = NonNullable<
    NonNullable<NewShopPageQuery['sanityShopPage']>['footerStatementLinks']
>[0];

function getLink(item: MenuItem, lang: Language): string {
    if (!item) {
        return '';
    }

    let linkItem = lang === 'en' && item.linkEn ? item.linkEn : item.linkNb;

    if (!linkItem) {
        return '';
    }

    if (linkItem.__typename === 'SanityContentPage') {
        const categoryPage = 'categoryPage' in linkItem ? linkItem.categoryPage?.[lang] : null;

        const categoryPath = getCategoryPath(categoryPage?.slug);

        return `/${lang}${categoryPath}/${linkItem.slug?.[lang]?.current}`;
    }
    if (linkItem.__typename === 'SanityLandingPage') {
        return `/${lang}/${linkItem.path}`;
    }
    return '';
}

const ColorFilters: FC<{
    colors: string[] | undefined;
    setColors: Dispatch<SetStateAction<string[] | undefined>>;
    uniqueColors: Maybe<Array<Maybe<ProductColors>>>;
}> = ({ colors: colors_, setColors, uniqueColors }) => {
    let filters = colors_ || [];
    const [showFilters_, setShowFilters] = useState(false);

    // Always show filters if a filter is set
    useEffect(() => {
        // Use colors_ instead of filters to avoid this being rerun every time the component renders
        if (colors_ && colors_.length > 0) {
            setShowFilters(true);
        }
    }, [colors_]);
    const showFilters = showFilters_ || filters.length > 0;

    const toggleFilter = (filter: string) => {
        setColors(oldColors_ => {
            let oldColors = oldColors_ || [];
            if (oldColors?.includes(filter)) {
                return oldColors.filter(el => el !== filter);
            } else {
                return [...oldColors, filter];
            }
        });
    };

    return (
        <div
            css={`
                margin-top: ${space(4)};
                margin-bottom: ${space(2)};
            `}
        >
            <button
                css={`
                    ${buttonReset};
                    ${weight.medium};
                    cursor: ${filters.length === 0 ? 'pointer' : 'default'};
                    color: ${constants.colors.pallet.black};
                `}
                onClick={() => setShowFilters(prev => !prev)}
            >
                <span
                    css={`
                        display: inline-block;
                        width: 16px;
                    `}
                >
                    {showFilters ? '-' : '+'}
                </span>
                <span>
                    <I18n>colorFilter</I18n>
                </span>
            </button>
            {showFilters && uniqueColors && (
                <div
                    css={`
                        overflow-x: auto;
                        white-space: nowrap;
                    `}
                >
                    <div
                        css={`
                            display: flex;
                            margin-top: ${space(2)};

                            button {
                                ${secondaryButton};
                                padding: 0 ${space(2)};
                                border-radius: 100px;
                                margin-right: ${space(1)};
                                transition: background 100ms ease-out;

                                &:hover {
                                    background: #f2f2f2;
                                }

                                span {
                                    /* Tweak because the font is not vertically centered */
                                    transform: translateY(-1.5px);
                                    display: inline-flex;
                                    align-items: center;

                                    span
                                }

                                &.active {
                                    border-color: #2b2929;
                                    background: #2b2929;
                                    color: #fff;
                                }
                            }
                        `}
                    >
                        {(uniqueColors || []).map(color => (
                            <button
                                key={color?.hex}
                                onClick={() => toggleFilter(color?.hex || '')}
                                className={filters.includes(color?.hex || '') ? 'active' : ''}
                            >
                                <span>
                                    {/* todo i18n title name */}
                                    {color?.title}
                                    <span
                                        css={`
                                            background: ${color?.hex};
                                            display: inline-block;
                                            width: 1rem;
                                            height: 1rem;
                                            border-radius: 100%;
                                            margin-left: ${space(1)};
                                        `}
                                    ></span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Filters: FC<{
    filters: string[] | undefined;
    setFilters: Dispatch<SetStateAction<string[] | undefined>>;
    data: NewShopPageQuery;
}> = ({ filters: filters_, setFilters, data }) => {
    let filters = filters_ || [];
    const lang = useLanguage();
    const [showFilters_, setShowFilters] = useState(false);

    // Always show filters if a filter is set
    useEffect(() => {
        // Use filters_ instead of filters to avoid this being rerun every time the component renders
        if (filters_ && filters_.length > 0) {
            setShowFilters(true);
        }
    }, [filters_]);
    const showFilters = showFilters_ || filters.length > 0;

    const toggleFilter = (filter: string) => {
        setFilters(oldFilters_ => {
            let oldFilters = oldFilters_ || [];
            if (oldFilters?.includes(filter)) {
                return oldFilters.filter(el => el !== filter);
            } else {
                return [...oldFilters, filter];
            }
        });
    };

    return (
        <div
            css={`
                border-bottom: 1px solid #d6d6d6;
                border-top: 1px solid #d6d6d6;
                @media ${desktopMedia} {
                    margin-top: ${space(6)};
                }
                margin-bottom: ${space(6)};
                padding-top: ${space(2)};
                padding-bottom: ${space(2)};
            `}
        >
            <button
                css={`
                    ${buttonReset};
                    ${weight.medium};
                    cursor: ${filters.length === 0 ? 'pointer' : 'default'};
                    color: ${constants.colors.pallet.black};
                `}
                onClick={() => setShowFilters(prev => !prev)}
            >
                <span
                    css={`
                        display: inline-block;
                        width: 16px;
                    `}
                >
                    {showFilters ? '-' : '+'}
                </span>{' '}
                Filter
            </button>
            {showFilters && (
                <div
                    css={`
                        overflow-x: auto;
                        white-space: nowrap;
                    `}
                >
                    <div
                        css={`
                            display: flex;
                            margin-top: ${space(2)};

                            button {
                                ${secondaryButton};
                                padding: 0 ${space(2)};
                                border-radius: 100px;
                                margin-right: ${space(1)};
                                transition: background 100ms ease-out;

                                span {
                                    /* Tweak because the font is not vertically centered */
                                    transform: translateY(-1.5px);
                                    display: block;
                                }

                                &:hover {
                                    background: #f2f2f2;
                                }

                                &.active {
                                    border-color: #2b2929;
                                    background: #2b2929;
                                    color: #fff;
                                }
                            }
                        `}
                    >
                        {data.allSanityCollection.nodes.map(collection => (
                            <button
                                onClick={() => toggleFilter(collection.slug?.[lang]?.current || '')}
                                key={collection.id}
                                className={
                                    filters.includes(collection.slug?.[lang]?.current || '')
                                        ? 'active'
                                        : ''
                                }
                            >
                                <span>
                                    <I18nStr>{collection.title}</I18nStr>
                                </span>
                            </button>
                        ))}
                        <button
                            onClick={() =>
                                toggleFilter(data.crowdsourcingTexts?.slug?.[lang]?.current || '')
                            }
                            className={
                                filters.includes(
                                    data.crowdsourcingTexts?.slug?.[lang]?.current || ''
                                )
                                    ? 'active'
                                    : ''
                            }
                        >
                            <span>
                                <I18nStr>{data.crowdsourcingTexts?.productPageTag || ''}</I18nStr>
                            </span>
                        </button>
                        <button
                            onClick={() =>
                                toggleFilter(data.crowdfundingTexts?.slug?.[lang]?.current || '')
                            }
                            className={
                                filters.includes(
                                    data.crowdfundingTexts?.slug?.[lang]?.current || ''
                                )
                                    ? 'active'
                                    : ''
                            }
                        >
                            <span>
                                <I18nStr>{data.crowdfundingTexts?.productPageTag || ''}</I18nStr>
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const Products: FC<{
    id: string;
    products?: Products;
    fakeProducts?: FakeProducts;
    gender?: GenderId;
}> = ({ id, products, fakeProducts, gender }) => {
    return (
        <div
            id={id}
            css={`
                display: grid;
                grid-auto-flow: row dense;
                grid-gap: ${space(10)} ${space(8)};
                grid-template-columns: 100%;
                margin-bottom: ${space(10)};

                @media (min-width: 800px) {
                    grid-template-columns: repeat(auto-fill, minmax(274px, 1fr));
                }

                @media (min-width: 1600px) {
                    grid-template-columns: repeat(3, 1fr);
                }
            `}
        >
            {products
                ? products.map(product => (
                      <ShopPageProductCard key={product.id} product={product} gender={gender} />
                  ))
                : null}
            {fakeProducts
                ? fakeProducts?.map(product => (
                      <ShopPageFakeProductCard key={product.id} fakeProduct={product} />
                  ))
                : null}
        </div>
    );
};

const ShopPageProductCard: FC<{ product: Product; gender?: GenderId }> = ({ product, gender }) => {
    const colspan = 1;

    return (
        <ProductCard
            css={`
                grid-column: span ${colspan};
            `}
            //TODO pass gender context or color context for image selection
            colspan={colspan}
            gender={gender}
            productCard={{ product }}
            imgSizes={`(min-width: 2625px) calc(641px * ${colspan}), (min-width: 1550px) calc(${colspan} * (((80vw - 48px) / 3) - 42px)), (min-width: 1240px) calc(${colspan} * 354px), (min-width: 998px) calc(${colspan} * (33vw - 48px/3 - 42px)), (min-width: 800px) calc(${colspan} * (50vw - 24px - 32px)), calc(${colspan} * (100vw - 48px))`}
        />
    );
};

const ShopPageFakeProductCard: FC<{ fakeProduct: FakeProduct }> = ({ fakeProduct }) => {
    const colspan = 1;
    return (
        <FakeProductCard
            css={`
                grid-column: span ${colspan};
            `}
            colspan={colspan}
            product={{
                title: fakeProduct.title,
                subtitle: fakeProduct.subtitle,
                image: fakeProduct.image,
                link: fakeProduct.link,
            }}
            imgSizes={`(min-width: 2625px) calc(641px * ${colspan}), (min-width: 1550px) calc(${colspan} * (((80vw - 48px) / 3) - 42px)), (min-width: 1240px) calc(${colspan} * 354px), (min-width: 998px) calc(${colspan} * (33vw - 48px/3 - 42px)), (min-width: 800px) calc(${colspan} * (50vw - 24px - 32px)), calc(${colspan} * (100vw - 48px))`}
        />
    );
};

export default ShopPageWrapper;

export const query = graphql`
    query NewShopPage {
        allSanityClothingType(sort: { priority: ASC }) {
            nodes {
                id
                title {
                    nb
                    en
                }
                slug {
                    nb {
                        current
                    }
                    en {
                        current
                    }
                }
                gender
            }
        }

        allSanityCollection(sort: { priority: ASC }) {
            nodes {
                id
                title {
                    nb
                    en
                }
                id
                slug {
                    nb {
                        current
                    }
                    en {
                        current
                    }
                }
            }
        }

        allProduct(
            sort: { sanityProduct: { priority: ASC } }
            filter: { visibility: { eq: "public" } }
        ) {
            nodes {
                id
                productState
                crowdfundingDate
                crowdfundingGoal
                slug {
                    en
                    nb
                }
                clothingType {
                    id
                    gender
                }
                productState
                centraId
                title {
                    en
                    nb
                }
                collection {
                    slug {
                        nb {
                            current
                        }
                        en {
                            current
                        }
                    }
                }
                sex
                colors {
                    hex
                    title
                }
                sanityProduct {
                    productCardTitle {
                        en
                        nb
                    }

                    productCardImageTag {
                        nb
                        en
                    }
                    title {
                        nb
                        en
                    }

                    images {
                        __typename

                        ... on SanityProductImage {
                            image: _rawImage
                            gender
                        }
                    }
                }
            }
        }
        allSanityFakeProduct(sort: { priority: ASC }) {
            nodes {
                id
                link {
                    en
                    nb
                }
                subtitle {
                    en
                    nb
                }
                image {
                    asset: _rawAsset
                }
                title {
                    en
                    nb
                }
                clothingType {
                    id
                }
            }
        }
        crowdfundingTexts: sanityProductStateTexts(productState: { eq: "crowdFunding" }) {
            productPageTag {
                nb
                en
            }
            slug {
                en {
                    current
                }
                nb {
                    current
                }
            }
        }
        crowdsourcingTexts: sanityProductStateTexts(productState: { eq: "crowdSourcing" }) {
            productPageTag {
                nb
                en
            }
            slug {
                en {
                    current
                }
                nb {
                    current
                }
            }
        }

        sanityShopPage {
            footerStatementHeader {
                nb
                en
            }

            footerStatementContent {
                nb
                en
            }

            footerStatementLinks {
                _key
                linkNb {
                    __typename
                    ... on SanityContentPage {
                        id
                        slug {
                            en {
                                current
                            }
                            nb {
                                current
                            }
                        }
                        categoryPage {
                            en {
                                ...categoryPage
                            }
                            nb {
                                ...categoryPage
                            }
                        }
                    }
                    ... on SanityLandingPage {
                        id
                        path
                    }
                }
                linkEn {
                    __typename
                    ... on SanityContentPage {
                        id
                        slug {
                            en {
                                current
                            }
                            nb {
                                current
                            }
                        }
                        categoryPage {
                            en {
                                ...categoryPage
                            }
                            nb {
                                ...categoryPage
                            }
                        }
                    }
                    ... on SanityLandingPage {
                        id
                        path
                    }
                }
                title {
                    en
                    nb
                }
            }
        }
    }
`;
